import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import moment from 'moment';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import {
  Autocomplete,
  Combobox,
  DateTime,
  Input,
  Hidden,
} from '~/components/Form';
import { DESCARGA_NF, SOBRAS_E_FALTAS } from '~/constants/modules';
import { t, can } from '~/helpers';
import {
  storeOperacaoDreno,
  updateOperacaoDreno,
} from '~/store/modules/operacaodreno/actions';

export default function FormDreno({ reference, data, create }) {
  const dispatch = useDispatch();
  const { find, params } = useSelector((state) => state.operacaodreno);
  const initials = create ? data : { ...(find || {}), ...data };
  const [posto, setPosto] = useState(initials.idposto || null);
  const [tanqueVirtual, setTanqueVirtual] = useState(
    initials.idtanquevirtual || null
  );
  const [produto, setProduto] = useState(initials.idproduto || null);
  const [tanqueFisico, setTanqueFisico] = useState(
    initials.idtanquefisico || null
  );

  useEffect(() => {
    const source = create ? data : find || {};

    if (!source || Object.keys(source).length === 0) return;

    setPosto(source.idposto || null);
    setTanqueVirtual(source.idtanquevirtual || null);
    if (
      source.idproduto !== undefined &&
      source.idproduto !== null &&
      source.idproduto !== ''
    ) {
      setProduto(source.idproduto);
    }
    setTanqueFisico(source.idtanquefisico || null);
  }, [
    create,
    data?.idposto,
    data?.idtanquevirtual,
    data?.idproduto,
    data?.idtanquefisico,
    find?.idposto,
    find?.idtanquevirtual,
    find?.idproduto,
    find?.idtanquefisico,
  ]);

  async function submit(values) {
    try {
      reference.current.setErrors({});
      const schema = Yup.object().shape({
        idposto: Yup.number()
          .typeError(t('s.geral.validacao.campoobrigatorio'))
          .required(t('s.geral.validacao.campoobrigatorio')),
        idtanquevirtual:
          can(DESCARGA_NF) || can(SOBRAS_E_FALTAS)
            ? Yup.string()
                .typeError(t('s.geral.validacao.campoobrigatorio'))
                .required(t('s.geral.validacao.campoobrigatorio'))
            : Yup.string().nullable(),
        idproduto: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        idtanquefisico: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        datahoradreno: Yup.mixed()
          .test(
            'datahoradreno-obrigatoria',
            t('s.geral.validacao.campoobrigatorio'),
            (value) =>
              value !== null &&
              value !== undefined &&
              String(value).trim() !== ''
          )
          .test(
            'datahoradreno-valida',
            t('s.geral.validacao.campoobrigatorio'),
            (value) => {
              if (
                value === null ||
                value === undefined ||
                String(value).trim() === ''
              )
                return true;
              if (moment.isMoment(value)) return value.isValid();
              return moment(
                value,
                ['DD/MM/YYYY HH:mm:ss', 'YYYY-MM-DD HH:mm:ss'],
                true
              ).isValid();
            }
          ),
        volumedreno: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .transform((value) => (Number.isNaN(value) ? undefined : value))
          .min(1, t('s.geral.validacao.valorminimo').replace('%s', 1))
          .max(
            999999,
            t('s.geral.validacao.tamanhomaximo').replace('%s', 999999)
          ),
        observacaodreno: Yup.string()
          .max(255)
          .required(t('s.geral.validacao.campoobrigatorio')),
        codigousuario: Yup.string()
          .trim()
          .required(t('s.geral.validacao.campoobrigatorio')),
      });
      const payload = {
        ...(create ? {} : find || {}),
        ...values,
        idposto: values.idposto || (!create ? find.idposto : undefined),
        idtanquevirtual:
          can(DESCARGA_NF) || can(SOBRAS_E_FALTAS)
            ? values.idtanquevirtual ||
              tanqueVirtual ||
              (!create ? find.idtanquevirtual : undefined)
            : null,
        idproduto:
          values.idproduto || produto || (!create ? find.idproduto : undefined),
        idtanquefisico:
          values.idtanquefisico ||
          tanqueFisico ||
          (!create ? find.idtanquefisico : undefined),
        datahoradreno:
          values.datahoradreno || (!create ? find.datahoradreno : undefined),
        volumedreno: Number(String(values.volumedreno).replace(',', '.')),
      };
      await schema.validate(payload, { abortEarly: false });
      if (create) dispatch(storeOperacaoDreno(payload, params));
      else
        dispatch(
          updateOperacaoDreno({ ...payload, iddreno: find.iddreno }, params)
        );
    } catch (err) {
      if (err instanceof Yup.ValidationError)
        reference.current.setErrors(
          err.inner.reduce((all, e) => ({ ...all, [e.path]: e.message }), {})
        );
    }
  }

  return (
    <Form ref={reference} initialData={initials} onSubmit={submit}>
      <Hidden name="idproduto" />
      <Autocomplete
        name="idposto"
        label="codigonomeposto"
        url="postoscomboios"
        unique="idposto"
        title={t('s.geral.postocomboio')}
        disabled={!create}
        getValue={(v) => {
          if (!v?.idposto) return;

          const novoPosto = v.idposto || null;
          setPosto(novoPosto);
          setTanqueVirtual(null);
          setProduto(null);
          setTanqueFisico(null);
          if (can(DESCARGA_NF) || can(SOBRAS_E_FALTAS)) {
            reference.current.setFieldValue('idtanquevirtual', '');
          }
          reference.current.setFieldValue('idtanquefisico', '');
          reference.current.setFieldValue('idproduto', '');
        }}
      />
      {(can(DESCARGA_NF) || can(SOBRAS_E_FALTAS)) && (

        <Combobox
          key={`tanque-virtual-${posto || 'vazio'}`}
          name="idtanquevirtual"
          label="tanquevirtualprodutotanquevirtual"
          url="tanquesvirtuais"
          unique="idtanquevirtual"
          title={t('s.configuracao.tanquefisico.idtanquevirtual')}
          disabled={!create || !posto}
          params={{
            wherefield: ['idposto'],
            valuewherefield: [posto],
            typewherefield: ['EQUALS'],
          }}
          getValue={(v) => {
            if (!v?.idtanquevirtual) return;

            const novoTanqueVirtual = v.idtanquevirtual || null;
            setTanqueVirtual(novoTanqueVirtual);
            setProduto(v.idproduto || null);
            reference.current.setFieldValue('idproduto', v.idproduto || '');
            setTanqueFisico(null);
            reference.current.setFieldValue('idtanquefisico', '');
          }}
        />
      )}
      <Combobox
        key={`tanque-fisico-${posto || 'vazio'}-${
          can(DESCARGA_NF) || can(SOBRAS_E_FALTAS)
            ? tanqueVirtual || 'vazio'
            : 'sem-tanque-virtual'
        }`}
        name="idtanquefisico"
        label="codigotanquefisico"
        url="tanquesfisicos"
        unique="idtanquefisico"
        title={t('s.configuracao.tanquefisico.idtanquefisico')}
        disabled={
          !create ||
          !posto ||
          ((can(DESCARGA_NF) || can(SOBRAS_E_FALTAS)) && !tanqueVirtual)
        }
        params={
          can(DESCARGA_NF) || can(SOBRAS_E_FALTAS)
            ? {
                wherefield: ['idposto', 'idtanquevirtual'],
                valuewherefield: [posto, tanqueVirtual],
                typewherefield: ['EQUALS', 'EQUALS'],
              }
            : {
                wherefield: ['idposto'],
                valuewherefield: [posto],
                typewherefield: ['EQUALS'],
              }
        }
        getValue={(v) => {
          if (!v?.idtanquefisico) return;
          setTanqueFisico(v.idtanquefisico);
          if (!(can(DESCARGA_NF) || can(SOBRAS_E_FALTAS))) {
            setProduto(v.idproduto || null);
            reference.current.setFieldValue('idproduto', v.idproduto || '');
          }
        }}
      />
      <DateTime
        name="datahoradreno"
        label={t('s.relatorio.auditoria.datahora')}
        time
        disabled={!create}
      />
      <Input
        name="volumedreno"
        label={t('s.operacao.dreno.volume')}
        type="number"
      />
      <Input
        name="observacaodreno"
        label={t('s.geral.observacao')}
        multiline
        rows={4}
        inputProps={{ maxLength: 255 }}
      />
      <Input
        name="codigousuario"
        label={t('s.menu.cadastro.usuario')}
        inputProps={{ maxLength: 100 }}
      />
    </Form>
  );
}

FormDreno.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};
FormDreno.defaultProps = { data: {} };
